import React from 'react'

// Components
import { AsyncButton, Tooltip } from '@sketch/components'
import { PlanPicker } from '../../components'
import BillSimulation from 'modules/workspace/components/BillSimulation'
import AddEditors from 'modules/workspace/components/AddEditors'

// GQL
import {
  BillingPlanFragment,
  useDraftBillingSummaryQuery,
  GetWorkspaceSubscribeInformationQuery,
} from '@sketch/gql-types'

// Styles
import {
  Wrapper,
  Container,
  PlanPickerWrapper,
  Section,
  BillSummaryWrapper,
  TextWithTooltip,
  TooltipContainer,
  TooltipBodyWrapper,
  TooltipText,
  QuestionMarkIcon,
} from './WorkspaceSubscribePlan.styles'
import {
  H1,
  H2,
} from 'modules/workspace/views/WorkspaceSubscribeView/WorkspaceSubscribeView.styles'

// Types
import { DiscountError } from '../../components/Discount'

export interface WorkspaceSubscribePlanProps {
  plans: BillingPlanFragment[]
  selectedPlan?: BillingPlanFragment
  onPlanChange: (plan: BillingPlanFragment) => void
  onPaymentDetails: () => Promise<void>
  customer: NonNullable<
    GetWorkspaceSubscribeInformationQuery['workspace']['customer']
  >
  numberEditors: number
  onEditorsChange?: (editors: number) => void
  discountCode?: string
  onValidDiscountCode: (discountCode: string) => void
  onClearDiscountCode?: () => void
  discountError?: DiscountError
  onUpdateDiscountError: (discountError?: DiscountError) => void
}

const TOOLTIP_TEXT = (
  <TooltipBodyWrapper>
    <TooltipText>
      <b>How Pricing Works</b>
    </TooltipText>
    <TooltipText>
      <b>Viewers</b> can browse, inspect and comment on documents in the web
      app. Adding Viewers is free!
    </TooltipText>
    <TooltipText>
      <b>Editors</b> can do all of the above, and create and edit documents in
      the Mac app. You can add more Editors to the Workspace later.
    </TooltipText>
  </TooltipBodyWrapper>
)

export const WorkspaceSubscribePlan: React.FC<WorkspaceSubscribePlanProps> = ({
  customer,
  plans,
  selectedPlan,
  onPlanChange,
  onPaymentDetails,
  numberEditors,
  onEditorsChange,
  discountCode,
  onValidDiscountCode,
  onClearDiscountCode,
  discountError,
  onUpdateDiscountError,
}) => {
  // Customer seats
  const numberOfExtraSeats = customer.billing!.seats.availableSeats

  const { data, error } = useDraftBillingSummaryQuery({
    variables: {
      planId: selectedPlan?.id || '',
      seats: numberEditors,
    },
    skip: !selectedPlan,
  })

  const editorCost = data
    ? `${data.draftBillingSummary.billingCycleAmount}` // Type casting to a string to avoid 0 being falsy
    : undefined
  const isDisabled = Boolean(!selectedPlan || editorCost === '0' || error)

  const handlePlanChange = (plan: BillingPlanFragment) => {
    onPlanChange(plan)
    onClearDiscountCode?.()
  }

  const handleContinueToPayment = async () => {
    // Clear any discount errors than can exist
    onUpdateDiscountError?.()

    onPaymentDetails()
  }

  return (
    <Wrapper>
      <H1>Set Up a Subscription</H1>

      <Container>
        <div>
          <Section>
            <H2>Choose Plan</H2>

            <TextWithTooltip>
              You only pay for users who edit Sketch documents.
              <TooltipContainer>
                <Tooltip content={TOOLTIP_TEXT}>
                  <QuestionMarkIcon />
                </Tooltip>
              </TooltipContainer>
            </TextWithTooltip>

            <PlanPickerWrapper>
              <PlanPicker
                plans={plans}
                onChange={handlePlanChange}
                selectedPlan={selectedPlan}
                isDisabled={editorCost === '0'}
              />
            </PlanPickerWrapper>
          </Section>
          <Section>
            <H2>Add Editors</H2>
            <AddEditors
              seats={numberEditors}
              onChange={editors => onEditorsChange?.(editors)}
            />
          </Section>
        </div>

        <div>
          <BillSummaryWrapper>
            <H2>Bill Summary</H2>
            <BillSimulation
              plan={selectedPlan!}
              trialEnd={customer.billing!.trialEnd!}
              numberEditors={numberEditors}
              numberExtraSeats={numberOfExtraSeats}
              showDiscountInput={false}
              discountCode={discountCode}
              onValidDiscountCode={onValidDiscountCode}
              onClearDiscountCode={onClearDiscountCode}
              discountError={discountError}
              onUpdateDiscountError={onUpdateDiscountError}
            />
          </BillSummaryWrapper>

          <AsyncButton
            variant="primary-untinted"
            fill
            onClick={handleContinueToPayment}
            disabled={isDisabled}
          >
            Continue to Payment
          </AsyncButton>
        </div>
      </Container>
    </Wrapper>
  )
}
